/**
 * FAQ Hub – client-side logic
 * Handles: view switching, search, keyword highlighting, scroll-to-target
 */
(function () {
  'use strict';

  class FAQHub {
    constructor(el) {
      this.el = el;
      this.sectionId = el.dataset.sectionId;

      // Views
      this.viewCategories = el.querySelector('[data-view="categories"]');
      this.viewDetail = el.querySelector('[data-view="detail"]');

      // Detail view elements
      this.detailTitle = el.querySelector('[data-detail-title]');
      this.faqItems = Array.from(el.querySelectorAll('.faq-hub__faq-item'));

      // Search elements
      this.searchInput = el.querySelector('.faq-hub__search-input');
      this.searchRow = el.querySelector('[data-search-row]');
      this.inlineResults = el.querySelector('.faq-hub__inline-results');
      this.resultsList = el.querySelector('.faq-hub__results-list');
      this.searchKeywordEl = el.querySelector('.faq-hub__search-keyword');

      // Category cards
      this.categoryCards = Array.from(el.querySelectorAll('.faq-hub__category-card'));

      // Back buttons
      this.backBtns = Array.from(el.querySelectorAll('.faq-hub__back-btn'));

      // Views wrapper (cached for height-lock)
      this.viewsWrapper = el.querySelector('.faq-hub__views-wrapper');

      // Current state
      this.currentCategory = null;
      this.searchDebounceTimer = null;

      // Cache FAQ data once (DOM doesn't change at runtime)
      this._faqDataCache = null;

      this._onDocumentClick = this._handleDocumentClick.bind(this);

      this._bindEvents();
    }

    _bindEvents() {
      document.addEventListener('click', this._onDocumentClick);

      // Category card click
      this.categoryCards.forEach(card => {
        card.addEventListener('click', (e) => {
          e.preventDefault();
          const categoryId = card.dataset.category;
          const categoryName = card.querySelector('.faq-hub__card-name').textContent;
          // Blur before DOM changes to avoid focus-scroll
          card.blur();
          this._showCategory(categoryId, categoryName);
        });
      });

      // Back buttons
      this.backBtns.forEach(btn => {
        btn.addEventListener('click', () => {
          btn.blur();
          const backTarget = btn.dataset.backView || 'categories';
          if (backTarget === 'detail' && this.currentCategory) {
            this._showCategory(this.currentCategory.id, this.currentCategory.name, null);
          } else {
            this._showView('categories');
          }
        });
      });

      // Mobile search row tap: focus input directly
      if (this.searchRow) {
        this.searchRow.addEventListener('click', (e) => {
          if (this.searchInput) this.searchInput.focus();
        });
      }

      // Search icon click: run search if input has value
      const searchIcon = this.el.querySelector('.faq-hub__search-icon');
      if (searchIcon) {
        searchIcon.style.cursor = 'pointer';
        searchIcon.addEventListener('click', (e) => {
          e.stopPropagation();
          if (this.searchInput) {
            const query = this.searchInput.value.trim();
            if (query.length >= 1) {
              clearTimeout(this.searchDebounceTimer);
              this._runSearch(query);
            } else {
              this.searchInput.focus();
            }
          }
        });
      }

      // Search input events
      if (this.searchInput) {
        this.searchInput.addEventListener('keydown', (e) => {
          if (e.key === 'Enter') {
            clearTimeout(this.searchDebounceTimer);
            this._runSearch(this.searchInput.value.trim());
          }
          if (e.key === 'Escape') {
            this.searchInput.value = '';
            this._hideInlineResults();
            if (this.searchRow) this.searchRow.classList.remove('is-active');
          }
        });
        this.searchInput.addEventListener('input', () => {
          clearTimeout(this.searchDebounceTimer);
          const query = this.searchInput.value.trim();
          if (query.length >= 2) {
            this.searchDebounceTimer = setTimeout(() => {
              this._runSearch(query);
            }, 350);
          } else if (query.length === 0) {
            this._hideInlineResults();
          }
        });
      }
    }

    _handleDocumentClick(e) {
      if (!this.el.contains(e.target)) {
        this._hideInlineResults();
      }
    }

    /* ---- View Management ---- */
    _showView(viewName) {
      // Lock wrapper height synchronously so page height doesn't change during view switch
      const w = this.viewsWrapper;
      if (w) w.style.height = w.offsetHeight + 'px';

      [this.viewCategories, this.viewDetail].forEach(v => {
        if (v) v.classList.remove('is-active');
      });
      const target = this.el.querySelector(`[data-view="${viewName}"]`);
      if (target) target.classList.add('is-active');

      // Release height lock after paint
      if (w) requestAnimationFrame(() => { w.style.height = ''; });
    }

    _hideInlineResults() {
      if (this.inlineResults) this.inlineResults.hidden = true;
      if (this.resultsList) this.resultsList.innerHTML = '';
    }

    /* ---- Category Detail ---- */
    _showCategory(categoryId, categoryName, scrollToFaqId) {
      this.currentCategory = { id: categoryId, name: categoryName };

      // Update detail title
      if (this.detailTitle) {
        this.detailTitle.textContent = categoryName;
      }

      // Show/hide FAQ items by category
      this.faqItems.forEach(item => {
        if (item.dataset.category === categoryId) {
          item.style.display = '';
        } else {
          item.style.display = 'none';
        }
      });

      if (scrollToFaqId) {
        // From search result: switch view then scroll to target
        this._showView('detail');
        requestAnimationFrame(() => {
          const target = this.el.querySelector(`[data-faq-id="${scrollToFaqId}"]`);
          if (target) {
            // Calculate sticky header height dynamically
            const stickyHeader = document.querySelector('header, .header, sticky-header, [data-sticky]');
            const headerH = (stickyHeader && getComputedStyle(stickyHeader).position === 'sticky')
              ? stickyHeader.offsetHeight
              : 0;
            const offset = headerH + 80;
            const targetY = target.getBoundingClientRect().top + window.scrollY - offset;
            window.scrollTo({ top: targetY, behavior: 'smooth' });
            target.classList.remove('is-target');
            void target.offsetWidth;
            target.classList.add('is-target');
            setTimeout(() => target.classList.remove('is-target'), 1600);
          }
        });
      } else {
        this._showView('detail');
      }
    }

    /* ---- Search ---- */
    _runSearch(query) {
      if (!query) return;

      const data = this._getFAQData(); // returns cached result
      const lowerQuery = query.toLowerCase();

      // Filter matching FAQ items
      const matches = data.faqs.filter(faq => {
        return (
          faq.question.toLowerCase().includes(lowerQuery) ||
          faq.answer.toLowerCase().includes(lowerQuery)
        );
      });

      // Sort: title (question) matches first, then answer-only matches
      matches.sort((a, b) => {
        const aInQ = a.question.toLowerCase().includes(lowerQuery) ? 0 : 1;
        const bInQ = b.question.toLowerCase().includes(lowerQuery) ? 0 : 1;
        return aInQ - bInQ;
      });

      this._renderSearchResults(matches, query);
      if (this.inlineResults) this.inlineResults.hidden = false;

      // Update keyword display
      if (this.searchKeywordEl) {
        this.searchKeywordEl.innerHTML = `Search results for: <strong>${this._escapeHtml(query)}</strong>`;
      }
    }

    _renderSearchResults(matches, query) {
      if (!this.resultsList) return;

      if (matches.length === 0) {
        this.resultsList.innerHTML = `<div class="faq-hub__no-results">No results found for "${this._escapeHtml(query)}"</div>`;
        return;
      }

      this.resultsList.innerHTML = matches.map(faq => {
        const hlQuestion = this._highlight(faq.question, query);
        const excerpt = this._getExcerpt(faq.answer, query);
        const hlExcerpt = this._highlight(excerpt, query);
        return `
          <div class="faq-hub__result-item"
               data-category-id="${faq.categoryId}"
               data-category-name="${this._escapeAttr(faq.categoryName)}"
               data-faq-id="${faq.id}">
            <div class="faq-hub__result-category">${this._escapeHtml(faq.categoryName)}</div>
            <div class="faq-hub__result-question">${hlQuestion}</div>
            <div class="faq-hub__result-excerpt">${hlExcerpt}</div>
          </div>
        `;
      }).join('');

      // Bind click events on results
      this.resultsList.querySelectorAll('.faq-hub__result-item').forEach(item => {
        item.addEventListener('click', () => {
          const catId = item.dataset.categoryId;
          const catName = item.dataset.categoryName;
          const faqId = item.dataset.faqId;
          this._hideInlineResults();
          this._showCategory(catId, catName, faqId);
        });
      });
    }

    /* ---- Data ---- */
    _getFAQData() {
      if (this._faqDataCache) return this._faqDataCache;
      // Collect FAQ data from DOM (runs once, then cached)
      const faqs = this.faqItems.map(item => ({
        id: item.dataset.faqId,
        categoryId: item.dataset.category,
        categoryName: item.dataset.categoryName || '',
        question: item.querySelector('.faq-hub__faq-question')?.textContent || '',
        answer: item.querySelector('.faq-hub__faq-answer')?.textContent || ''
      }));
      this._faqDataCache = { faqs };
      return this._faqDataCache;
    }

    /* ---- Helpers ---- */
    _highlight(text, query) {
      if (!query || !text) return this._escapeHtml(text);
      const escaped = this._escapeHtml(text);
      const escapedQuery = query.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
      const re = new RegExp(`(${escapedQuery})`, 'gi');
      return escaped.replace(re, '<mark class="faq-hub__highlight">$1</mark>');
    }

    _getExcerpt(text, query, maxLength = 160) {
      const lower = text.toLowerCase();
      const idx = lower.indexOf(query.toLowerCase());
      if (idx === -1) {
        return text.slice(0, maxLength) + (text.length > maxLength ? '…' : '');
      }
      const start = Math.max(0, idx - 60);
      const end = Math.min(text.length, idx + query.length + 100);
      return (start > 0 ? '…' : '') + text.slice(start, end) + (end < text.length ? '…' : '');
    }

    _escapeHtml(str) {
      return String(str)
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/"/g, '&quot;');
    }

    _escapeAttr(str) {
      return String(str).replace(/"/g, '&quot;').replace(/'/g, '&#39;');
    }
  }

  // Init all FAQ Hub sections on page
  function initFAQHubs() {
    document.querySelectorAll('[data-section-type="faq-hub"]').forEach(el => {
      if (!el._faqHubInstance) {
        el._faqHubInstance = new FAQHub(el);
      }
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initFAQHubs);
  } else {
    initFAQHubs();
  }

  // Shopify theme editor support
  document.addEventListener('shopify:section:load', function (e) {
    const el = e.target.querySelector('[data-section-type="faq-hub"]');
    if (el) {
      el._faqHubInstance = new FAQHub(el);
    }
  });
})();
